<script setup lang="ts">
import CardHeading from "../CardHeading.vue";
import { computed } from "vue";
import { Calculator } from "@lucide/vue";

import { useCalculationTypes } from "@/dataProviders/CalculationTypes";
import { useWizardProject } from "@/features/BuildingProject/composables/useWizardProject";

import { WizardProjectType, services as BuildingProjectServices } from "@/features/BuildingProject";

import StepIntro from "../StepIntro/StepIntro.vue";
import CalculationTypeForm from "../CalculationPrerequisites/CalculationTypeForm.vue";
import LifecycleStageStrip from "@/features/BuildingProject/components/LifecycleStageStrip.vue";
import InfoIcon from "@/features/Tooltip/InfoIcon.vue";

const props = defineProps<{
  wasValidated: boolean;
  project: WizardProjectType;
}>();

const calculationTypes = useCalculationTypes();

const { project, lens } = useWizardProject(() => props.project);

const includedPhasesTranslated = lens(
  (current) => current.emissionCalculationAssumptions.emissionMetaData.includedPhasesTranslated,
  (current, value) => (current.emissionCalculationAssumptions.emissionMetaData.includedPhasesTranslated = value)
);
const includedPhases = lens(
  (current) => current.emissionCalculationAssumptions.emissionMetaData.includedPhases,
  (current, value) => (current.emissionCalculationAssumptions.emissionMetaData.includedPhases = value)
);

/**
 * Feeds both LifecycleStageStrip's `editable` prop and the empty-stages error below. Same "is this row
 * Custom" check CalculationPrerequisites.vue's own `isDisabled` uses (find Custom's row by label, compare
 * its id), so the create validator (createWizardSteps.ts), the strip's editable gate and the error banner
 * can never disagree about which type unlocks stage editing.
 */
const isCustomCalculationType = computed(() => {
  const calculationTypeEnum = project.value.emissionCalculationAssumptions.calculationType.calculationTypeEnum;
  return (
    calculationTypeEnum !== null &&
    calculationTypeEnum !== undefined &&
    calculationTypes.getCalculationTypes().find((type) => type.label === "Custom")?.id === calculationTypeEnum
  );
});

/**
 * Keyed on being Custom rather than `calculationTypeEnum === 0`, unlike CalculationPrerequisites.vue's own
 * version of this banner: that hardcode only matches a country whose Custom row happens to be id 0
 * (Sweden's is 20). validateCalculationPrerequisitesForCreate uses the same fixed rule for the actual block.
 */
const hasEmptyStagesError = computed(
  () => props.wasValidated && (includedPhasesTranslated.value?.length ?? 0) <= 0 && isCustomCalculationType.value
);

/** Same pairing CalculationPrerequisites.vue writes on a stage toggle: translated labels plus the enum. */
const handlePhasesChanged = (included: Array<string>) => {
  // use Set to create unique Array
  const uniqueIncluded = [...new Set(included)];
  includedPhasesTranslated.value = uniqueIncluded;
  includedPhases.value = BuildingProjectServices.includedPhasesConversion.getIncludedPhasesAsEnum(uniqueIncluded);
};

/** One chip toggled in or out, from LifecycleStageStrip's `editable` mode. See CalculationPrerequisites.vue. */
const toggleStage = (value: string) => {
  const current = includedPhasesTranslated.value ?? [];
  handlePhasesChanged(current.includes(value) ? current.filter((stage) => stage !== value) : [...current, value]);
};
</script>

<template>
  <div class="w-full">
    <StepIntro
      class="mb-6"
      heading="Which calculation applies?"
      hint="The calculation type sets which stages are included, and those decide the remaining steps." />

    <!-- Split out of ProjectDetails.vue into its own step (RTLCA-5661 follow-up), so Building area can sit
         between the project's basics and its calculation instead of trailing both. Same card, unchanged. -->
    <div class="rounded-8 bg-slate-0 p-px-20 @container flex flex-col gap-4 border border-slate-300">
      <div
        class="-mx-px-20 -mt-px-20 px-px-20 pt-px-20 rounded-t-8 flex flex-col gap-1 border-b border-slate-300 bg-slate-50 pb-3">
        <div class="flex items-center gap-2">
          <span class="bg-app-blue-50 rounded-4 flex shrink-0 items-center justify-center p-1">
            <Calculator
              class="icon-sm text-app-blue-600"
              :stroke-width="1.5"
              aria-hidden="true" />
          </span>
          <CardHeading class="t-sh4 text-type-default">Calculation</CardHeading>
        </div>
      </div>

      <CalculationTypeForm
        :project="project"
        :was-validated="wasValidated"
        :compact="true"
        :show-heading="false" />

      <div
        v-if="project.emissionCalculationAssumptions.calculationType.calculationTypeEnum !== null"
        class="flex flex-col gap-2">
        <div class="flex items-center gap-2">
          <span class="t-caption text-type-muted">Life-cycle stages</span>
          <InfoIcon
            tooltip="Select which life cycle stages or modules are included in the calculation. The chosen stages define the system boundary and directly affect the total calculated emissions. These will be predefined for some calculation methods. If you want to change them, choose the Custom calculation type." />
        </div>

        <div
          v-if="hasEmptyStagesError"
          class="bg-app-red-300/20 text-app-red-700 rounded-16 t-small flex flex-col gap-2 p-4">
          It's not possible to create a project without selecting any stages
        </div>

        <LifecycleStageStrip
          :included-stages="project.emissionCalculationAssumptions.emissionMetaData.includedPhasesTranslated"
          :editable="isCustomCalculationType"
          size="md"
          @toggle-stage="toggleStage" />
      </div>
    </div>
  </div>
</template>
